import { Box, Checkbox, ClickAwayListener, InputBase, Paper, Popper, TextField } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TreeUtils } from '../../../utils/TreeUtils';
import { PeriodicityButton } from './PeriodicityButton';

export const DimTree = ({
  displayName, // Label of the TreeSelect
  disabled, // Whether this tree is disabled. TODO: Disabled should still be able to open dropdown
  treeData, // Array of objects containing id, name, and children
  selectedIds, // Array of selected IDs (leaf nodes only)
  onFilterChange, // A function that will be called when the user changes the values. This component will pass the new selected leaf IDs into the function
  disabledIds, // Optional array of node IDs whose checkboxes are disabled (e.g. out of scope, or already ledgered by another group)
  variant = 'outlined', // The MUI TextField variant for the select input ('outlined' | 'filled')
  size = 'medium', // The MUI TextField size for the select input ('medium' | 'small')
  error, // Whether the field is in an error state (red outline + helperText)
  helperText, // Helper / error text shown beneath the field
  onBlur, // Called when the dropdown closes (blur-like), e.g. to mark a form field touched

  // Optional for dimensions that have Periodicities - These are simply passed through to PeriodicityButton
  periodicities, // A list of the periodicities for this dimension
  selectedPeriodicities, // A list of the periodicities that are currently selected
  onPeriodicityChange, // A function that will be called when the user changes the periodicities. This component will pass the new periodicities into the function
  multi = true,
}) => {
  // ----- State -----

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState([]);
  const anchorRef = useRef(null);

  // Close the dropdown, firing the blur-like onBlur so callers can mark the field touched.
  const close = () => {
    setOpen(false);
    onBlur?.();
  };

  // ----- Initialization -----

  const { idToNode, leafIds } = useMemo(() => {
    return TreeUtils.parseTree(treeData);
  }, [treeData]);

  const disabledIdSet = useMemo(() => new Set(disabledIds ?? []), [disabledIds]);

  // On initialization, the root node should be expanded
  useEffect(() => {
    const rootId = treeData.id;
    setExpanded((prev) => (prev.length === 0 ? [rootId] : prev));
  }, [treeData]);

  // ----- Selection -----

  const descendantLeafIdsFromId = (id) => {
    const node = idToNode[id];
    if (node.children?.length > 0) {
      const descendantLeafIds = [];
      node.children.forEach((childNode) => {
        descendantLeafIds.push(...descendantLeafIdsFromId(childNode.id));
      });
      return descendantLeafIds;
    } else {
      return [id];
    }
  };

  // Tri-state math ignores disabled leaves so a parent can still be "fully selected" when its
  // only unselected descendants are disabled (and therefore unselectable).
  const enabledDescendantLeafIds = (id) => descendantLeafIdsFromId(id).filter((leafId) => !disabledIdSet.has(leafId));

  const isFullySelected = (id) => {
    const descendantLeafIds = enabledDescendantLeafIds(id);
    return descendantLeafIds.length > 0 && descendantLeafIds.every((id) => selectedIds.includes(id));
  };

  const isPartiallySelected = (id) => {
    const descendantLeafIds = enabledDescendantLeafIds(id);
    return (
      !descendantLeafIds.every((id) => selectedIds.includes(id)) &&
      descendantLeafIds.some((id) => selectedIds.includes(id))
    );
  };

  // Calculates new set of selectedIds and calls onChange (prop from parent)
  const handleCheckboxClick = (id) => {
    if (!multi) {
      onFilterChange([id]);
      return;
    }
    const newSelectedIds = [...selectedIds];

    // Determine the target leaves for this toggle, excluding any disabled leaves
    const candidateIds = leafIds.includes(id) ? [id] : descendantLeafIdsFromId(id);
    const targetIds = candidateIds.filter((targetId) => !disabledIdSet.has(targetId));
    if (targetIds.length === 0) return;

    // If all descendant leaves are already selected, then we are removing
    const removing = targetIds.every((targetId) => selectedIds.includes(targetId));

    targetIds.forEach((leafId) => {
      if (removing) {
        newSelectedIds.splice(newSelectedIds.indexOf(leafId), 1);
      } else if (newSelectedIds.indexOf(leafId) === -1) {
        newSelectedIds.push(leafId);
      }
    });

    onFilterChange(newSelectedIds);
  };

  // ----- Rendering -----

  // Returns the tree that will that will be rendered after filtering the nodes by the search
  // This function also fills out the array of autoExpandIds
  const buildVisibleTree = (nodes, search, autoExpandedIds) => {
    const lowerCaseSearch = search.toLowerCase();
    return nodes.reduce((accumulated, node) => {
      const selfHit =
        node.name.toLowerCase().includes(lowerCaseSearch) ||
        node.code.toLowerCase().includes(lowerCaseSearch) ||
        node.id.toString().toLowerCase() === lowerCaseSearch;
      const children = node.children ? buildVisibleTree(node.children, search, autoExpandedIds) : [];
      if (selfHit || children.length) {
        if (search) autoExpandedIds.push(node.id);
        accumulated.push({ ...node, children: selfHit ? (node.children ?? []) : children });
      }
      return accumulated;
    }, []);
  };

  const autoExpandedIds = [];
  const visibleTree = buildVisibleTree([treeData], search, autoExpandedIds);
  const expandedIds = search ? Array.from(new Set([...expanded, ...autoExpandedIds])) : expanded;

  // Recursively render nodes of the tree
  const renderItems = (nodes) => {
    return nodes.map((node) => {
      const hasChildren = !!node.children?.length;
      const checked = hasChildren && multi ? isFullySelected(node.id) : selectedIds.includes(node.id);
      const indeterminate = hasChildren && isPartiallySelected(node.id);
      const selectable = node.selectable === undefined || node.selectable !== false;
      // A leaf is disabled if explicitly listed; a parent is disabled only when all its descendants are.
      const nodeDisabled = hasChildren ? enabledDescendantLeafIds(node.id).length === 0 : disabledIdSet.has(node.id);
      return (
        <TreeItem
          key={node.id}
          itemId={node.id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={checked}
                indeterminate={indeterminate}
                disabled={nodeDisabled || !selectable}
                onClick={(event) => event.stopPropagation()}
                onChange={() => handleCheckboxClick(node.id)}
              />
              {node.name}
            </Box>
          }
        >
          {hasChildren && renderItems(node.children)}
        </TreeItem>
      );
    });
  };

  // Collapse fully selected parents into their own name so e.g. selecting a brand shows the brand, not its leaf count
  const collectSelectedNames = (node) =>
    selectedIds.includes(node.id) || descendantLeafIdsFromId(node.id).every((id) => selectedIds.includes(id)) ?
      [node.name]
    : (node.children ?? []).flatMap(collectSelectedNames);

  // The text that is displayed in the TextField is dependent on what nodes are selected.
  // "All" only reads as a summary when there is more than one choice to select among, so a
  // single-select tree, or one with a lone leaf, names what is selected instead.
  let textFieldContent = '';
  if (selectedIds.length === 0) {
    textFieldContent = 'None';
  } else if (multi && leafIds.length > 1 && selectedIds.length === leafIds.length) {
    textFieldContent = 'All';
  } else {
    // The root collapses to its own name whenever everything is selected, which is the "All" case, so
    // naming starts below it.
    const roots = treeData.children?.length ? treeData.children : [treeData];
    const selectedNames = roots.flatMap(collectSelectedNames);
    textFieldContent = selectedNames.length <= 3 ? selectedNames.join(', ') : `${selectedIds.length} selected`;
  }

  return (
    <>
      <TextField
        ref={anchorRef}
        fullWidth
        variant={variant}
        size={size}
        label={displayName}
        value={textFieldContent}
        error={error}
        helperText={helperText}
        onClick={() => (open ? close() : setOpen(true))}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: periodicities && (
              <PeriodicityButton
                periodicities={periodicities}
                selectedPeriodicities={selectedPeriodicities}
                onPeriodicityChange={onPeriodicityChange}
                closeFilterDropdown={() => setOpen(false)}
                anchorRef={anchorRef}
                dimDisplayName={displayName}
              />
            ),
          },
        }}
        disabled={disabled}
      />

      <Popper
        open={open && !disabled}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[
          // Flip above the field when there isn't enough room below, then keep the panel within
          // the viewport so it never gets clipped by the screen edge.
          { name: 'flip', enabled: true, options: { fallbackPlacements: ['top-start'] } },
          { name: 'preventOverflow', enabled: true, options: { altAxis: true, padding: 8 } },
        ]}
      >
        <ClickAwayListener onClickAway={close}>
          <Paper sx={{ width: anchorRef.current?.clientWidth || 300, p: 1, maxHeight: 600, overflow: 'auto' }}>
            {/* Search */}
            <Box sx={{ mb: 1, px: 1, py: 0.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
              <InputBase
                fullWidth
                placeholder="Search…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={disabled}
              />
            </Box>

            {/* Tree */}
            <SimpleTreeView expandedItems={expandedIds} onExpandedItemsChange={(event, ids) => setExpanded(ids)}>
              {renderItems(visibleTree)}
            </SimpleTreeView>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
